import { FormSection, Toggle } from '@/components';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { disableDocumentFileShare, enableDocumentFileShare } from '../../api';
import { FormSectionInfo } from '@/components/FormSection';

interface DocumentShareSectionProps {
  fileId: string;
  // Defined when the file already has a public share link; undefined means sharing is off.
  initialShareLink: string | undefined;
}

/**
 * @component DocumentShareSection
 * @description Renders a toggle for enabling/disabling the public share link of a document file.
 * When sharing is active the current share URL is shown alongside a "Copy Link" button.
 * Each toggle change immediately calls the API; the toggle is disabled while the request is pending.
 *
 * @props
 *   fileId (string, required) - The ID of the document file.
 *   initialShareLink (string | undefined, required) - Share URL received from the server on load;
 *     undefined means sharing is currently off.
 */
export function DocumentShareSection({ fileId, initialShareLink }: DocumentShareSectionProps) {
  const { t } = useTranslation();

  const [shareLink, setShareLink] = useState<string | undefined>(initialShareLink);
  const [isLoading, setIsLoading] = useState(false);

  const isShared = shareLink !== undefined;

  const handleToggle = async (enabled: boolean) => {
    setIsLoading(true);
    try {
      if (enabled) {
        const response = await enableDocumentFileShare(fileId);
        setShareLink(response.shareLink);
      } else {
        await disableDocumentFileShare(fileId);
        setShareLink(undefined);
      }
    } catch {
      toast.error(
        enabled
          ? t('documents.detail.share.enableError')
          : t('documents.detail.share.disableError')
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyLink = () => {
    if (!shareLink) return;
    navigator.clipboard.writeText(shareLink);
    toast.success(t('documents.detail.share.linkCopied'));
  };

  return (
    <FormSection icon="eye" title={t('documents.detail.share.title')}>
      <FormSectionInfo>
        {t('documents.detail.share.description')}
      </FormSectionInfo>
      <div className="flex items-center gap-3">
        <Toggle
          checked={isShared}
          onChange={(checked) => void handleToggle(checked)}
          disabled={isLoading}
          ariaLabel={t('documents.detail.share.toggleLabel')}
        />
        <span className="text-sm text-onsurface">
          {t('documents.detail.share.label')}
        </span>

        {isShared && (
          <button
            type="button"
            onClick={handleCopyLink}
            className="ml-auto text-sm font-medium text-primary hover:underline"
          >
            {t('documents.detail.share.copyLink')}
          </button>
        )}
      </div>
    </FormSection>
  );
}
